import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { MapPin, Loader2 } from "lucide-react";
import MediaUploader from "@/components/MediaUploader";
import PlanMarkerPicker from "@/components/PlanMarkerPicker";
import { TicketForm } from "@/components/PositionDrawer";

function SetMarker({ ticket, onDone }) {
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState("");
  const [marker, setMarker] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/overview/plans").then((r) => setPlans((r.data || []).filter((p) => p.project_id === ticket.project_id))).catch(() => setPlans([]));
  }, [ticket.project_id]);
  const save = async () => {
    setBusy(true);
    try {
      const { data: pos } = await api.post("/positions", { plan_id: planId, marker, title: ticket.title || "" });
      try { await api.put(`/tickets/${ticket.id}/position`, { position_id: pos.id }); }
      catch (err) { await api.delete(`/positions/${pos.id}`).catch(() => {}); throw err; }
      toast.success(`Marker ${pos.number} gesetzt`);
      onDone(pos);
    } catch (e) { toast.error("Marker konnte nicht gesetzt werden"); } finally { setBusy(false); }
  };
  return (
    <div className="border border-slate-200 rounded-xl p-3 space-y-2" data-testid="ticket-set-marker">
      <div className="text-xs font-semibold text-slate-600 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />Marker im Bauplan setzen (optional)</div>
      <div><Label className="text-xs">Bauplan</Label>
        <Select value={planId} onValueChange={(v) => { if (v) { setPlanId(v); setMarker(null); } }}>
          <SelectTrigger data-testid="ticket-marker-plan"><SelectValue placeholder={plans.length ? "Bauplan wählen" : "Keine Baupläne im Projekt"} /></SelectTrigger>
          <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{[p.building_name, p.floor_name, p.name].filter(Boolean).join(" · ")} ({p.position_count || 0} Pos.)</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {planId && <PlanMarkerPicker planId={planId} value={marker} onChange={setMarker} />}
      {marker && <Button size="sm" onClick={save} disabled={busy} data-testid="ticket-marker-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Marker übernehmen"}</Button>}
    </div>
  );
}

export default function ProjectTicketDialog({ ticket, onClose, onChanged, onOpenPlan }) {
  if (!ticket) return null;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="project-ticket-dialog">
        <DialogHeader><DialogTitle>{ticket.ticket_number} · {ticket.project_name || "Projekt"}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Ticket ohne Position im Bauplan.</p>
        <TicketForm ticket={ticket} projectId={ticket.project_id} onSaved={() => { onChanged(); onClose(); }} onCancel={onClose} />
        <SetMarker ticket={ticket} onDone={(pos) => { onChanged(); onClose(); onOpenPlan && onOpenPlan(pos); }} />
        <div className="text-xs font-semibold text-slate-600">Fotos hinzufügen</div>
        <MediaUploader links={{ ticket_id: ticket.id, project_id: ticket.project_id }} onUploaded={onChanged} />
      </DialogContent>
    </Dialog>
  );
}
